import { QuestionEntry } from '@wix/ambassador-faq-question-entry-v2-question-entry/types';
import React, { memo, useMemo } from 'react';
import { Text } from '@wix/wix-ui-tpa/cssVars';
import RCV from '../../../RCV';
import { st, classes } from '../../styles.st.css';
import { DraftJSObject } from '../../../../../../types/RichContent';
import { useStyles } from '@wix/tpa-settings/react';
import { AccordionCSS } from '../../Interfaces';
import { useEnvironment } from '@wix/yoshi-flow-editor';
import SocialWrapper from '../../../SocialWrapper/SocialWrapper';
import { DATA_HOOKS } from '../../../../../../constants/dataHooksElements';
import stylesParams from '../../../../stylesParams';
import { useLayoutType } from '../../../../../../hooks/useLayoutType';

function areQuestionsEqual(
  prev: QuestionEntry[],
  next: QuestionEntry[],
): boolean {
  return (
    prev === next ||
    (prev.length === next.length && prev.every((q, i) => q.id === next[i]?.id))
  );
}

interface ListItemRowProps {
  item: QuestionEntry;
  index: number;
  columnId: string;
  itemAppearance: string;
  isMobile: boolean;
  questionFontParam: { htmlTag?: string };
}

const ListItemRow = memo(function ListItemRow({
  item,
  index,
  columnId,
  itemAppearance,
  isMobile,
  questionFontParam,
}: ListItemRowProps) {
  const { id, question, draftjs = null, plainText = null } = item;
  const content = useMemo<DraftJSObject | string>(
    () => (draftjs ? JSON.parse(draftjs) : plainText ?? ''),
    [draftjs, plainText],
  );
  return (
    <div
      className={st(classes.listWrapperBorder, {
        itemAppearance,
        mobile: isMobile,
      })}
      id={id!}
      role="listitem"
    >
      <div className={st(classes.listWrapperAccordion)} tabIndex={0}>
        <Text
          tagName={questionFontParam.htmlTag}
          key={`question-${id}`}
          className={st(classes.question, { mobile: isMobile })}
          data-hook={DATA_HOOKS.LIST_ITEM_QUESTION(columnId + index)}
        >
          {question!}
        </Text>
        <Text
          key={`answer-${id}`}
          className={st(classes.answer, { mobile: isMobile })}
          data-hook={DATA_HOOKS.LIST_ITEM_ANSWER(columnId + index)}
          role="region"
        >
          {content && (
            <RCV convertAnswerToJSON={content} answerQuestionId={id!} />
          )}
        </Text>
      </div>
      <SocialWrapper questionId={id!} />
    </div>
  );
});

const ListOneColumnComponent = ({
  filteredQuestions,
}: {
  filteredQuestions: QuestionEntry[];
}) => {
  const styles = useStyles();
  const { isMobile } = useEnvironment();
  const {
    itemAppearance,
    spacing: { gap },
  } = useLayoutType();
  const questionFontParam = styles.get?.(stylesParams.questionTextFont);
  // Extract settings for item appearance and animation

  const customStyle = {
    '--item-gap': `${gap}px`,
  } as AccordionCSS;

  const columnId = filteredQuestions[0]?.id || '';
  return (
    <div
      style={customStyle}
      className={st(classes.listLayout)}
      role="list"
      data-hook={DATA_HOOKS.WIDGET_LIST_ONE_COLUMN(columnId)}
    >
      {filteredQuestions.map((item: QuestionEntry, index) => (
        <ListItemRow
          key={item.id!}
          item={item}
          index={index}
          columnId={columnId}
          itemAppearance={itemAppearance}
          isMobile={isMobile}
          questionFontParam={questionFontParam}
        />
      ))}
    </div>
  );
};

const ListOneColumn = memo(ListOneColumnComponent, (prev, next) =>
  areQuestionsEqual(prev.filteredQuestions, next.filteredQuestions),
);

ListOneColumn.displayName = 'ListOneColumn';

export default ListOneColumn;
